/**
 * ATC discovery + override wiring (REQ-CHK-1/2; research/02 §8).
 *
 * findProductForm — verbatim from v1 common_utils.js: the three-rule
 * selection over every form[action*="/cart/add"], including
 * rule-2-beats-rule-1 within a form and last-form-wins for the custom
 * selector. BUNDLED-jQuery iteration (DIV-004; v1 used the theme's).
 *
 * DIV-014: quick-add card forms are excluded from candidacy before the
 * three-rule selection runs. Dawn-family themes (e.g. Edge) render a
 * "quick-add-<id>" form inside a ".quick-add" wrapper on every product
 * recommendation / sibling-product card, each with its own [type=submit]
 * button and a real (but DIFFERENT product's) variant id. Those forms can
 * precede the actual product form in DOM order, so rule 1's first-match-wins
 * picked a recommendation card's form as `productForm` — the live oldId
 * re-read in HandleAddToCartEvent then adopted that unrelated product's
 * variant id (Crisp session_3bd311f5, Lindenprinthouse/WallBoon — Edge
 * theme's sibling-products block on the product page, though the quick-add
 * cards it collided with were unrelated "You may also like" cards, not the
 * siblings block itself). If excluding quick-add forms leaves no candidate
 * at all, `findProductForm` falls back to the unfiltered selection (ATC-FORM-011)
 * — this exclusion can only ever fix the wrong-variant bug, never turn a
 * page that used to render into one that hits the null-productForm throw
 * below (ERRATA #9) when it didn't before.
 *
 * HandleProductFormAndAddToCartButton — verbatim from v1
 * process_product_page.js: null productForm THROWS (unhandled render
 * rejection, ERRATA #9); submit listener with preventDefault +
 * stopImmediatePropagation + stopPropagation; ssw-product-form/
 * cpc-calculator-form classes; button listener-stripping clone;
 * window.cpc.{addToCartHandler,productForm,submitButton}; remove+add click
 * pair with default options (not "guaranteed").
 */
import { completeClientPerformanceFailure } from "../performance/client-performance-lifecycle";
import { appState } from "../state/app-state";
import { HandleAddToCartEvent } from "./add-to-cart";
import { UpdatePrice } from "../pricing";
import $ from "jquery";

// DIV-014: candidate forms, quick-add cards excluded unless explicitly
// overridden. The exclusion lives ENTIRELY in this selector string — never as
// extra imperative checks inside the `.each()` below — so that a page with no
// quick-add forms runs through byte-identical per-form work to the pre-DIV-014
// bundle. That matters: the characterization suite pins price-ticker/image-swap
// state captured mid-render (research/04 §4.4's firstLoad, `UpdatePrice`'s
// `data-origMoney` write), and even a no-op-outcome per-iteration statement
// added here measurably shifts when those synchronous writes land relative to
// the harness's MutationObserver flush — verified failing ELEM-CHECKBOX-004,
// ELEM-IMGSEL-006 (x2), ELEM-RADIO-004, PRODLOAD-019, PRODLOAD-024c on CI with
// an imperative-check version of this same exclusion, none of which have any
// quick-add markup in their fixtures.
const FORM_SELECTOR_WITHOUT_QUICK_ADD =
  'form[action*="/cart/add"][data-cpc-product-form="true"], ' +
  'form[action*="/cart/add"]:not([id^="quick-add"]):not(.quick-add *)';
const FORM_SELECTOR_ANY = 'form[action*="/cart/add"]';

function selectProductForm(
  addToCartSelector: string,
  selector: string | JQuery,
): { productForm: any; submitButton: any } {
  let productForm: any = null;
  let submitButton: any = null;

  // DIV-040 passes an already-filtered jQuery set; the per-form body is unchanged.
  const forms = typeof selector === "string" ? $(selector) : selector;
  forms.each(function (i: any, e: any) {
    const isCpcFormOverride = $(e).data("cpc-product-form");

    if (
      e.querySelector('[type="submit"]') &&
      (submitButton === null ||
        submitButton === undefined ||
        (isCpcFormOverride !== undefined && isCpcFormOverride == true))
    ) {
      submitButton = e.querySelector('[type="submit"]') || null;
      productForm = e;
    }
    if (
      e.querySelector('button[class*="cart"]') &&
      (submitButton === null ||
        submitButton === undefined ||
        (isCpcFormOverride !== undefined && isCpcFormOverride == true))
    ) {
      submitButton = e.querySelector('button[class*="cart"]') || null;
      productForm = e;
    }
    if (
      addToCartSelector.trim().length > 0 &&
      e.querySelector(addToCartSelector)
    ) {
      submitButton = e.querySelector(addToCartSelector) || null;
      productForm = e;
    }
  });

  return { productForm, submitButton };
}

// DIV-040: a form's `id` property is unreliable (a child named "id" — Shopify's
// variant input — shadows it), so the id ATTRIBUTE is what gets inspected.
const formId = (form: Element): string => form.getAttribute("id") ?? "";
// Secondary product forms a theme renders on the same page: quick-view modals,
// sticky add-to-cart bars, "frequently bought together" blocks. Only a HINT —
// used when the primary visibility test cannot decide (see findProductForm).
const SECONDARY_FORM_ID = /quick-view|quickview|sticky|frequently_bought|frequently-bought/i;
const candidateButton = (form: Element): Element | null =>
  form.querySelector('[type="submit"]') || form.querySelector('button[class*="cart"]');
// Laid out = has a box. A display:none ancestor (quick-view modal), a collapsed
// sticky bar or a not-yet-rendered block all give zero client rects; a visible
// position:fixed bar does NOT (which is why offsetParent is not used here).
const isLaidOut = (el: Element | null): boolean => el !== null && el.getClientRects().length > 0;

/**
 * DIV-040 (issue #206, Eurus): the three-rule selection picks the FIRST
 * candidate form, and a theme can render several `/cart/add` forms before the
 * real product form — Eurus puts its quick-view section's form, a sticky-bar
 * form and "frequently bought together" forms on every product page, and the
 * first of them won. When the chosen button has no layout box but another
 * candidate's does, the visible candidates are re-selected with the SAME
 * three rules; if the chosen form is still a secondary-shaped one and a
 * non-secondary candidate exists, those are re-selected. Both steps keep
 * every explicit override: a `data-cpc-product-form` form or a form matched by
 * the merchant's `addToCartSelector` is returned untouched. A page with one
 * candidate, or whose first candidate is visible and not secondary-shaped,
 * runs exactly the pre-DIV-040 code path.
 */
function preferVisibleProductForm(
  addToCartSelector: string,
  selector: string,
  chosen: { productForm: any; submitButton: any },
): { productForm: any; submitButton: any } {
  const form = chosen.productForm as Element;
  if ($(form).data("cpc-product-form") == true) return chosen;
  if (addToCartSelector.trim().length > 0 && form.querySelector(addToCartSelector)) return chosen;
  const candidates = $(selector);
  if (candidates.length < 2) return chosen;

  let result = chosen;
  if (!isLaidOut(result.submitButton)) {
    const visible = candidates.filter((_: number, f: Element) => isLaidOut(candidateButton(f)));
    if (visible.length > 0) {
      const picked = selectProductForm(addToCartSelector, visible);
      if (picked.productForm !== null) result = picked;
    }
  }
  if (SECONDARY_FORM_ID.test(formId(result.productForm))) {
    const primary = candidates.filter((_: number, f: Element) => !SECONDARY_FORM_ID.test(formId(f)));
    if (primary.length > 0) {
      const picked = selectProductForm(addToCartSelector, primary);
      if (picked.productForm !== null) result = picked;
    }
  }
  if (result !== chosen) console.log("CPC: product form re-selected (DIV-040)", formId(result.productForm));
  return result;
}

export function findProductForm(addToCartSelector = ""): {
  productForm: any;
  submitButton: any;
} {
  const withoutQuickAdd = selectProductForm(addToCartSelector, FORM_SELECTOR_WITHOUT_QUICK_ADD);
  if (withoutQuickAdd.productForm !== null) return preferVisibleProductForm(addToCartSelector, FORM_SELECTOR_WITHOUT_QUICK_ADD, withoutQuickAdd);
  // DIV-014 safety net: excluding quick-add forms left no candidate at all
  // (a page with only quick-add cards, or a form that happens to match the
  // quick-add convention for an unrelated reason). Falling back to the
  // unfiltered selection means this exclusion can only ever fix the
  // wrong-variant bug — it can never turn a page that used to render into
  // one that throws (ERRATA #9 / ATC-FORM-005) when it didn't before.
  return selectProductForm(addToCartSelector, FORM_SELECTOR_ANY);
}

/** DIV-041: does the theme handle this button's click with Alpine.js (`@click` / `x-on:click`)? */
export function isAlpineClickButton(button: Element | null): boolean {
  if (!button) return false;
  for (const attr of Array.from(button.attributes)) {
    if (attr.name === "@click" || attr.name.startsWith("@click.") || attr.name === "x-on:click" || attr.name.startsWith("x-on:click.")) return true;
  }
  return false;
}

export async function HandleProductFormAndAddToCartButton(addToCartSelector: any): Promise<any> {

  const { productForm: foundProductForm, submitButton: foundSubmitButton } = findProductForm(addToCartSelector);
  const defaultFormClasses = ["ssw-product-form", "cpc-calculator-form"];

  appState.productForm = foundProductForm;
  let submitButton = foundSubmitButton;

  // Telemetry only. No product form on the page is the single cause behind
  // ~99% of runtime_error reports (2026-09-21..23: 18.7k rows, 53 stores —
  // seven of them PAYING stores whose calculator has never rendered for a
  // shopper, because the theme's buy button is not [type=submit] or the
  // product is not purchasable). Until now every one of them arrived as a
  // bare `TypeError`, indistinguishable from any other crash. Name it, so the
  // admin views can list the stores whose theme needs an AddToCartSelector.
  // The throw on the next line is unchanged (BUG-010 / ERRATA #9 parity):
  // the report is latched here, the rejection still escapes as before, and
  // the outer handler's report is a no-op.
  if (appState.productForm == null) {
    completeClientPerformanceFailure("runtime_error", "NoProductForm");
  }

  //Update Base Variant if Id found inside it
  const oldIdInput = (appState.productForm as any).querySelector('input[name="oldId"]');

  // Keep baseVariantId in sync when the theme announces a variant change.
  // Themes that update the renamed oldId element silently are covered by the
  // point-of-use re-read in HandleAddToCartEvent.
  document.addEventListener("change", function (e: any) {
    if (e.target && e.target.name === "oldId" && e.target.value && e.target.value !== "0") {
      appState.baseVariantId = e.target.value;
      console.log("CPC: baseVariantId updated via change event to", appState.baseVariantId);
    }
  });
  document.addEventListener("variant:changed", function (e: any) {
    if (e.detail && e.detail.variant && e.detail.variant.id) {
      appState.baseVariantId = e.detail.variant.id;
      console.log("CPC: baseVariantId updated via variant:changed to", e.detail.variant.id);

      // DIV-033: keep #variantIdCustom's VALUE current too — see
      // buildVariantElement's comment. Never remove its `name='id'`, only
      // refresh what it holds.
      var variantIdCustomEl1 = document.getElementById("variantIdCustom") as HTMLInputElement | null;
      if (variantIdCustomEl1) variantIdCustomEl1.value = String(e.detail.variant.id);

      // When the customer picks a different Shopify variant (e.g. a different
      // width on a cut-to-length runner), update cpc_product_default_price so
      // that formulas using shopify_product_price reflect the selected variant's
      // price rather than the page-load default (variants[0].price).
      // Fixes: price display goes stale on products with 2+ Shopify variants
      // when the formula contains shopify_product_price (e.g. "shopify_product_price * 0.05").
      if (typeof e.detail.variant.price === "number" && e.detail.variant.price >= 0) {
        appState.cpc_product_default_price = e.detail.variant.price / 100;
        console.log("CPC: cpc_product_default_price updated via variant:changed to", appState.cpc_product_default_price);
        UpdatePrice();
      }

      // DIV-015: `submitButton` here closes over the SAME `let` binding this
      // function reassigns to the clone below, so by the time this listener
      // ever runs, it is already the clone. Sync its disabled state to the
      // theme's OWN authoritative signal instead of leaving whatever value
      // the clone inherited at creation time.
      //
      // The clone is a snapshot: `el.cloneNode(true)` below copies the native
      // button's `disabled` attribute exactly as it stands at that instant.
      // Dawn-family themes commonly disable that button for a brief in-flight
      // window during a variant/swatch change (updating price/media), then
      // re-enable the SAME node once resolved. If a shopper's swatch click
      // lands during that window while the calculator is still booting, the
      // clone is born disabled — and stays that way forever, because the
      // theme's later re-enable call targets the original node, which this
      // function has already replaced in the DOM (Crisp ticket, KITO GmbH,
      // session 4c27671f: "ATC button becomes unclickable if you pick a
      // different colour before the calculator has loaded").
      //
      // `variant:changed` firing means the theme just resolved a real
      // variant, so its `available` field is authoritative right now: apply
      // it. A genuinely unavailable variant keeps reporting `available:false`
      // and stays correctly disabled — this does not touch that case. A
      // theme that never sends `available` leaves the button exactly as
      // every prior version of this function did (guarded by the typeof
      // check), so this is additive only.
      if (typeof e.detail.variant.available === "boolean") {
        submitButton.disabled = !e.detail.variant.available;
      }
    }
  });

  // DIV-028: Horizon 4.1.0+ themes (Shopify's @shopify/events system) never
  // fire variant:changed at all — they dispatch ProductSelectEvent instead,
  // so the listener above never runs and cpc_product_default_price stays
  // stuck at the boot-time price forever (issue #164;
  // ScatterMats/96vezb-ez.myshopify.com — 33/97 products showed the wrong
  // price after a width switch once the theme moved to Horizon 4.1.0,
  // single-variant products unaffected since there's nothing to switch to).
  // ProductSelectEvent's variant JSON resolves asynchronously, after the
  // theme's AJAX section-rendering fetch completes, via `e.promise` — never
  // synchronously off `e.detail` the way variant:changed's is.
  //
  // The event's TYPE is "shopify:product:select", not "product:select":
  // Shopify's @shopify/events (cdn.shopify.com/storefront/standard-events.js)
  // prefixes every standard event name with "shopify:" (ShopifyStandardEvent
  // base class). Confirmed against the live ScatterMats theme's
  // variant-picker.js / standard-events.js — `this.dispatchEvent(new
  // ProductSelectEvent(...))` on the <variant-picker> element with
  // eventName = "shopify:product:select", bubbles:true so a document-level
  // listener does receive it once the name is correct.
  document.addEventListener("shopify:product:select", function (e: any) {
    e.promise &&
      e.promise.then(function (result: any) {
        var resource = result && result.detail && result.detail.resource;
        if (!resource) return;

        if (resource.id) {
          appState.baseVariantId = resource.id;
          console.log("CPC: baseVariantId updated via shopify:product:select to", resource.id);

          // Same DIV-033 #variantIdCustom sync as the variant:changed
          // listener above, for themes that only ever fire product:select.
          var variantIdCustomEl2 = document.getElementById("variantIdCustom") as HTMLInputElement | null;
          if (variantIdCustomEl2) variantIdCustomEl2.value = String(resource.id);
        }

        // Same shopify_product_price staleness fix as the variant:changed
        // listener above, for themes that only ever fire product:select.
        if (typeof resource.price === "number" && resource.price >= 0) {
          appState.cpc_product_default_price = resource.price / 100;
          console.log("CPC: cpc_product_default_price updated via shopify:product:select to", appState.cpc_product_default_price);
          UpdatePrice();
        }

        // Same DIV-015 availability sync as the variant:changed listener
        // above, for themes that only ever fire product:select.
        if (typeof resource.available === "boolean") {
          submitButton.disabled = !resource.available;
        }
      });
  });

  // Reset re-entrancy guard when the page is restored from bfcache (browser
  // back-button navigation after a successful ATC redirect to /cart).
  // Without this, cpc_isCheckoutInProgress stays true from the previous ATC,
  // silently blocking every subsequent ATC attempt on the restored page.
  window.addEventListener('pageshow', function(e: PageTransitionEvent) {
    if (e.persisted) {
      appState.cpc_isCheckoutInProgress = false;
      console.log('CPC: bfcache page restore — reset cpc_isCheckoutInProgress');
    }
  }, { once: false });

  (appState.productForm as any).addEventListener("submit", function (t: any) {
    t.preventDefault();
    t.stopImmediatePropagation();
    t.stopPropagation();
  });

  (appState.productForm as any).classList.add(...defaultFormClasses);

  var el = submitButton;
  // DIV-041 (issue #206): an Alpine.js theme (Eurus, Impact, …) adds to cart
  // from a click handler declared ON the button (`@click="addToCart(...)"`),
  // not from a form submit listener. Cloning would drop that handler (and a
  // clone is re-bound by Alpine anyway, unpredictably). The original button is
  // kept; CPC listens in the CAPTURE phase so it still runs first and blocks
  // the theme's handler until the price token is ready — then
  // SubmitViaThemeNativeSubmit re-dispatches the click for the theme.
  const alpineHandled = isAlpineClickButton(el);
  if (!alpineHandled) {
    let elClone = el.cloneNode(true);
    if (!!el && !!el.parentNode) el.parentNode.replaceChild(elClone, el);
    else el.replaceWith(elClone);
    submitButton = elClone;
  }
  // Non-enumerable, like window.cpc.$: diagnostics, not part of the enumerated window.cpc surface.
  Object.defineProperty(window.cpc, "alpineSubmitButton", { value: alpineHandled, enumerable: false, configurable: true, writable: true });

  // DIV-032: the clone just made above captures whatever `disabled` state the
  // theme's NATIVE button happens to be in at this exact instant. DIV-015
  // already found this can be born disabled by a transient theme-side
  // in-flight update and stay that way forever, with the theme's own later
  // re-enable call landing on the now-detached original node — but that fix
  // only corrects it reactively, the next time `variant:changed` or
  // `shopify:product:select` fires. A single-variant product (nothing to
  // switch to) or a multi-variant shopper who never touches the picker never
  // fires either event, so the clone can stay stuck disabled for the entire
  // visit with no recovery path (ScatterMats — Genesis/Bidar/Chino "by the
  // meter" runners, Crisp session 3ff76805: standalone Add to Cart button
  // rendered disabled despite the (single) variant being in stock; the
  // wrong-price half of the same report is DIV-029). Sync it once, right now,
  // to the boot-time variant's ALREADY-resolved true availability
  // (DIV-029/fetchProductMetaData) instead of trusting the snapshot the clone
  // was born with. A theme whose product JSON has no `available` field at all
  // leaves the button exactly as before (typeof guard, same as DIV-015).
  if (typeof appState.cpc_product_default_available === "boolean") {
    submitButton.disabled = !appState.cpc_product_default_available;
    console.log("CPC: submitButton.disabled synced at boot (DIV-032) to", submitButton.disabled);
  }

  window.cpc.addToCartHandler = HandleAddToCartEvent;
  window.cpc.productForm = appState.productForm;
  window.cpc.submitButton = submitButton;
  submitButton.removeEventListener("click", HandleAddToCartEvent, alpineHandled);
  submitButton.addEventListener("click", HandleAddToCartEvent, alpineHandled);

  console.log("render-block", submitButton);

  return submitButton;
}
